import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import dayjs from "dayjs";
import { organizerCreateWaitlistApi, organizerDeleteWaitlistApi, organizerListWaitlistApi } from "../../api/api";
import { beautyListServicesApi } from "../../api/organizerBeautyApi";
import { ALLOWED_DURATIONS, SPORT_LABELS } from "../../constants/courtDomain";
import { idDeVariante, tieneVariantes } from "../../helpers/beautyLineas";
import { FRANJAS, contactoResumen, criteriosResumen, franjaLabel, mensajeDeLista, origenLabel } from "./listaEsperaModel";

/* Lista de espera del día en la agenda del organizador (Beauty, canchas y mesas). Un botón "Lista de espera (N)" abre
   el panel con las anotaciones activas del día; desde ahí se anota a alguien (llamó, está en el mostrador) o se lo quita.
   Solo se pinta si el local activó la lista en Políticas (`enabled`). Los permisos los resuelve el back (403 -> aviso).
   `kind`: "beauty" | "court" | "table". `profesionales` ([{ id, name }]) solo para Beauty. */

const Estado = ({ children }) => (
  <p style={{ margin: 0, color: "#6b7280", fontSize: 13 }}>{children}</p>
);

const ListaEsperaDelDia = ({ enabled, barId, kind, date, profesionales = [], sports, onToast }) => {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");
  const [servicios, setServicios] = useState([]);
  const [form, setForm] = useState(null); // null = cerrado
  const [aviso, setAviso] = useState("");
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    if (!enabled || !barId || !date) return;
    const id = ++requestRef.current;
    setState("loading");
    setMessage("");
    try {
      const res = await organizerListWaitlistApi({ barId, date });
      if (id !== requestRef.current) return;
      setEntries(Array.isArray(res?.data?.info?.entries) ? res.data.info.entries : []);
      setState("success");
    } catch (e) {
      if (id !== requestRef.current) return;
      setMessage(mensajeDeLista(e, "No pudimos cargar la lista de espera. Probá de nuevo."));
      setState("error");
    }
  }, [enabled, barId, date]);

  useEffect(() => {
    setEntries(null);
    setAviso("");
    load();
  }, [load]);

  useEffect(() => {
    if (kind !== "beauty" || !enabled) return undefined;
    let vivo = true;
    beautyListServicesApi()
      .then((res) => {
        if (vivo) setServicios((Array.isArray(res?.data?.info) ? res.data.info : []).filter((s) => s.active !== false));
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, [kind, enabled]);

  if (!enabled) return null;

  const avisar = (texto) => (onToast ? onToast(texto) : setAviso(texto));

  const quitar = async (entry) => {
    try {
      await organizerDeleteWaitlistApi(entry.id);
      avisar("Anotación quitada");
      await load();
    } catch (e) {
      avisar(mensajeDeLista(e, "No pudimos quitar la anotación. Probá de nuevo."));
    }
  };

  const total = entries ? entries.length : 0;
  const etiqueta = entries ? `Lista de espera (${total})` : "Lista de espera";

  return (
    <div className="lde-dia" style={{ margin: "12px 0" }}>
      <button type="button" className="zr-btn zr-btn--soft" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {etiqueta}
      </button>
      {open && (
        <div className="zr-panel" style={{ marginTop: 12, padding: 16 }} role="region" aria-label="Lista de espera del día">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <strong>Lista de espera del {dayjs(date).format("DD/MM/YYYY")}</strong>
            <button type="button" className="zr-btn zr-btn--dark" onClick={() => setForm({})}>Anotar</button>
          </div>
          {aviso && <div role="status" style={{ marginBottom: 8, fontSize: 13 }}>{aviso}</div>}
          {state === "loading" && !entries && <Estado>Cargando la lista de espera…</Estado>}
          {state === "error" && (
            <div className="org-page-error">
              <p>{message}</p>
              <button type="button" onClick={load}>Reintentar</button>
            </div>
          )}
          {entries && entries.length === 0 && state !== "error" && (
            <Estado>Nadie está anotado para este día. Cuando alguien se anote desde la web, o lo anotes vos, aparece acá.</Estado>
          )}
          {entries && entries.length > 0 && (
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
              {entries.map((entry) => (
                <li key={entry.id} style={{ display: "flex", justifyContent: "space-between", gap: 12, borderTop: "1px solid #e5e7eb", paddingTop: 10 }}>
                  <div>
                    <div><strong>{entry.contact?.name || "Sin nombre"}</strong> <span style={{ color: "#6b7280" }}>{contactoResumen(entry)}</span></div>
                    <div style={{ fontSize: 13 }}>
                      {[criteriosResumen(entry, servicios), franjaLabel(entry.timeWindow), origenLabel(entry.source)].filter(Boolean).join(" · ")}
                    </div>
                    {Array.isArray(entry.notifiedSlots) && entry.notifiedSlots.length > 0 && (
                      <div style={{ fontSize: 12, color: "#6b7280" }}>Avisado por: {entry.notifiedSlots.join(", ")}</div>
                    )}
                  </div>
                  <button type="button" className="zr-btn zr-btn--soft" aria-label={`Quitar a ${entry.contact?.name || "la persona"}`} onClick={() => quitar(entry)}>
                    Quitar
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {form && (
        <AnotarModal
          barId={barId}
          kind={kind}
          date={date}
          servicios={servicios}
          profesionales={profesionales}
          sports={sports}
          onClose={() => setForm(null)}
          onDone={async () => {
            setForm(null);
            avisar("Anotado en la lista de espera");
            await load();
          }}
        />
      )}
    </div>
  );
};

const AnotarModal = ({ barId, kind, date, servicios, profesionales, sports, onClose, onDone }) => {
  const deportes = sports && sports.length ? sports : Object.keys(SPORT_LABELS);
  const [serviceId, setServiceId] = useState("");
  const [variantId, setVariantId] = useState("");
  const [professionalId, setProfessionalId] = useState("");
  const [sport, setSport] = useState("");
  const [duration, setDuration] = useState(String(ALLOWED_DURATIONS[0]));
  const [partySize, setPartySize] = useState("2");
  const [timeWindow, setTimeWindow] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const servicio = servicios.find((s) => String(s._id) === serviceId);
  const variantes = tieneVariantes(servicio);

  const criteria = (() => {
    if (kind === "beauty") {
      if (!serviceId || (variantes && !variantId)) return null;
      return {
        items: [{ serviceId, ...(variantId ? { variantId } : {}), addOnIds: [] }],
        ...(professionalId ? { professionalId } : {}),
      };
    }
    if (kind === "court") return { ...(sport ? { sport } : {}), durationMinutes: Number(duration) };
    const personas = Number(partySize);
    return Number.isInteger(personas) && personas > 0 ? { partySize: personas } : null;
  })();

  const valido = Boolean(criteria) && name.trim().length > 0 && (phone.trim().length > 0 || email.trim().length > 0);

  const enviar = async (event) => {
    event.preventDefault();
    if (!valido || sending) return;
    setSending(true);
    setError("");
    try {
      await organizerCreateWaitlistApi({
        barId,
        kind,
        date,
        criteria,
        ...(timeWindow ? { timeWindow } : {}),
        contact: { name: name.trim(), phone: phone.trim(), email: email.trim() },
      });
      await onDone();
    } catch (e) {
      setError(mensajeDeLista(e, "No pudimos anotar a la persona. Probá de nuevo."));
      setSending(false);
    }
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h2" style={{ fontSize: 18 }}>Anotar en la lista de espera</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={enviar} style={{ display: "grid", gap: 8 }}>
          <p style={{ margin: 0, fontSize: 13 }}>Día: {dayjs(date).format("DD/MM/YYYY")}</p>
          {kind === "beauty" && (
            <>
              <label htmlFor="lde-servicio">Servicio</label>
              <select id="lde-servicio" className="form-select" value={serviceId} onChange={(e) => { setServiceId(e.target.value); setVariantId(""); }}>
                <option value="">Elegí un servicio</option>
                {servicios.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
              </select>
              {variantes && (
                <>
                  <label htmlFor="lde-variante">Variante</label>
                  <select id="lde-variante" className="form-select" value={variantId} onChange={(e) => setVariantId(e.target.value)}>
                    <option value="">Elegí una variante</option>
                    {servicio.variants.map((v) => <option key={idDeVariante(v)} value={idDeVariante(v)}>{v.name}</option>)}
                  </select>
                </>
              )}
              {profesionales.length > 0 && (
                <>
                  <label htmlFor="lde-profesional">Profesional (opcional)</label>
                  <select id="lde-profesional" className="form-select" value={professionalId} onChange={(e) => setProfessionalId(e.target.value)}>
                    <option value="">Cualquiera</option>
                    {profesionales.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </>
              )}
            </>
          )}
          {kind === "court" && (
            <>
              <label htmlFor="lde-deporte">Deporte</label>
              <select id="lde-deporte" className="form-select" value={sport} onChange={(e) => setSport(e.target.value)}>
                <option value="">Cualquiera</option>
                {deportes.map((s) => <option key={s} value={s}>{SPORT_LABELS[s] || s}</option>)}
              </select>
              <label htmlFor="lde-duracion">Duración</label>
              <select id="lde-duracion" className="form-select" value={duration} onChange={(e) => setDuration(e.target.value)}>
                {ALLOWED_DURATIONS.map((d) => <option key={d} value={d}>{d} min</option>)}
              </select>
            </>
          )}
          {kind === "table" && (
            <>
              <label htmlFor="lde-personas">Personas</label>
              <input id="lde-personas" type="number" min="1" className="form-control" value={partySize} onChange={(e) => setPartySize(e.target.value)} />
            </>
          )}
          <label htmlFor="lde-franja">Franja (opcional)</label>
          <select id="lde-franja" className="form-select" value={timeWindow} onChange={(e) => setTimeWindow(e.target.value)}>
            {FRANJAS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          <label htmlFor="lde-nombre">Nombre</label>
          <input id="lde-nombre" className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
          <label htmlFor="lde-telefono">Teléfono (WhatsApp)</label>
          <input id="lde-telefono" type="tel" className="form-control" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <label htmlFor="lde-email">Email</label>
          <input id="lde-email" type="email" className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} />
          <small style={{ color: "#6b7280" }}>Dejá al menos un teléfono o un email para avisarle.</small>
          {error && <div role="alert" style={{ color: "#b91c1c", fontSize: 13 }}>{error}</div>}
          <button type="submit" className="zr-btn zr-btn--dark" disabled={!valido || sending}>
            {sending ? <Spinner animation="border" size="sm" /> : "Anotar"}
          </button>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default ListaEsperaDelDia;
